/**
 * @see https://github.com/IdentityModel/oidc-client-js/wiki
 */

import { Logger, SigninRedirectArgs, User, UserManager, WebStorageStateStore, } from 'oidc-client-ts';
import Mutex from '@/components/Mutex';

const mutex = new Mutex();

class EnhancedUserManager extends UserManager {
  /**
   * Get the user who is logged in.
   * If the user was deleted (expired) it restarts authentication.
   */
  async getValidUser() {
    const self = this as EnhancedUserManager;

    return mutex.runExclusive(() => new Promise((resolve: (u: User) => void, reject: (e: unknown) => void) => {
      self.getUser().then(
        async (user) => {
          if (user == null || user.expired) {
            // not actually used atm
            if (!window.tokenChecked) {
              Logger.info('oidc', 'starting first login');
              self.signinRedirect()
                .then(() => Logger.info('oidc', 'login started'));
              return reject(user);
            }

            Logger.info('oidc', 'renewing silently...');
            return self.signinSilent().then((u) => {
              if (u) {
                Logger.info('oidc', 'renewed silently');
                return resolve(u);
              }
              Logger.info('oidc', 'silent-renew failed. starting login');
              self.signinRedirect().then(() => Logger.info('oidc', 'login started'));
              return reject(user);
            });
          }
          Logger.debug('oidc', 'have valid token');
          return resolve(user);
        },
        (err) => reject(err),
      );
    }));
  }

  /**
   * call user-info endpoint
   */
  async userInfo(accessToken: string): Promise<User | undefined> {
    const url = (await this.metadataService.getMetadata()).userinfo_endpoint;
    if (!url) {
      Logger.error('oidc', 'userinfo not set up', this.settings);
      return undefined;
    }

    return fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}`, },
    }).then(r => r.json())
      .catch((e) => {
        Logger.error('oidc', 'user-info failed', e);
        return undefined;
      });
  }

  async startLogin(state: string, locale?: string) {
    // Logger.debug("[oidc ] start login...")
    return this.signinRedirect({
      state,
      ui_locales: locale,
    } as SigninRedirectArgs);
  }
}

const { APP_CONFIG } = window;
const userManager = new EnhancedUserManager({
  userStore: new WebStorageStateStore(),
  authority: APP_CONFIG.oauth.discoveryEndpoint,
  client_id: APP_CONFIG.oauth.clientId,
  redirect_uri: APP_CONFIG.oauth.redirectUri,
  response_type: APP_CONFIG.oauth.responseType,
  scope: APP_CONFIG.oauth.scopes,
  post_logout_redirect_uri: APP_CONFIG.oauth.post_logout_redirect_uri,
  // silent_redirect_uri: APP_CONFIG.oauth.silentRedirectUri,
  accessTokenExpiringNotificationTimeInSeconds: 10,
  automaticSilentRenew: false,
  filterProtocolClaims: false,
  loadUserInfo: false,
  response_mode: 'query',
});

userManager.events.addAccessTokenExpiring(() => {
  Logger.debug('oidc', 'AccessToken Expiring!');
});

userManager.events.addAccessTokenExpired(() => {
  Logger.warn('oidc', 'AccessToken Expired');
  /* userManager.removeUser().then(() => {
    Logger.info('oidc', 'removed expired user from store');
  }); */
});

userManager.events.addUserSignedOut(() => {
  Logger.debug('oidc', 'User sign out');
  userManager.removeUser().then(() => {
    Logger.info('oidc', 'removed user from store on sign-out');
  });
});

// userManager.events.addSilentRenewError((err) => {
//   Logger.error('oidc', 'Silent Renew Error', err);
// });

export default userManager;
